import { groupBy, sumBy } from 'lodash';
import { useCallback, useState } from 'react';
import uuid from 'react-uuid';
import { api } from 'src/constants/apiRoutes';
import useAsync from 'src/hooks/useAsync';
import axiosInstance from 'src/utils/axios';

export interface AccrualReportResponse {
  glCdId: number;
  glCd: string;
  glCdDesc: string;
  supplierName: string;
  poNumber: string;
  poId: number;
  poTotal: number;
  accrualAmount: number;
  grniItemCount: number;
  grniDocCount: number;
  lineItemNumber: string;
  poLineItemNumber: string;
  amountInPeriod: number;
}

export interface FormattedLineItem {
  poLineItemNumber: string;
  poId: number;
  supplierName: string;
  grniItemCount: number;
  grniDocCount: number;
  poTotal: number;
  amountInPeriod: number;
  accrualAmount: number;
}

export interface FormattedPO {
  glCdId: number;
  poNumber: string;
  poId: number;
  supplierName: string;
  grniItemCount: number;
  grniDocCount: number;
  poTotal: number;
  amountInPeriod: number;
  accrualAmount: number;
  lineItems: FormattedLineItem[];
  expanded: boolean;
  rowId: string;
}

export interface FormattedReportResponse {
  glCd: string;
  glCdDesc: string;
  glCdId: number;
  pos: FormattedPO[];
  grniItemCount: number;
  grniDocCount: number;
  poTotal: number;
  amountInPeriod: number;
  accrualAmount: number;
  expanded: boolean;
  rowId: string;
}

const useGetAccrualReportApi = (companyId: string, glPeriodId: number) => {
  const [rawAccrualData, setRawAccrualData] = useState<AccrualReportResponse[]>([]);
  const call = useCallback(async () => {
    try {
      const res = await axiosInstance.get<AccrualReportResponse[]>(
        api.getAccrualReport(companyId, glPeriodId)
      );
      setRawAccrualData(res?.data);

      const data: FormattedReportResponse[] = Object.values(groupBy(res.data, 'glCd')).map(
        (glCodeItems) => {
          const pos: FormattedPO[] = Object.values(groupBy(glCodeItems, 'poId')).map((poItems) => {
            const lineItems = poItems.map((item) => ({
              ...item,
              rowId: uuid(),
              poTotal: item.lineItemNumber === 'DISCOUNT' ? -item.poTotal : item.poTotal,
            }));
            return {
              glCdId: poItems[0].glCdId,
              poId: poItems[0].poId,
              poNumber: poItems[0].poNumber,
              supplierName: poItems[0].supplierName,
              grniItemCount: sumBy(lineItems, 'grniItemCount'),
              grniDocCount: sumBy(lineItems, 'grniDocCount'),
              poTotal: sumBy(lineItems, 'poTotal'),
              amountInPeriod: sumBy(lineItems, 'amountInPeriod'),
              accrualAmount: sumBy(lineItems, 'accrualAmount'),
              lineItems,
              expanded: false,
              rowId: uuid(),
            };
          });
          return {
            glCd: glCodeItems[0].glCd,
            glCdDesc: glCodeItems[0].glCdDesc,
            glCdId: glCodeItems[0].glCdId,
            pos,
            grniItemCount: sumBy(pos, 'grniItemCount'),
            grniDocCount: sumBy(pos, 'grniDocCount'),
            poTotal: sumBy(pos, 'poTotal'),
            amountInPeriod: sumBy(pos, 'amountInPeriod'),
            accrualAmount: sumBy(pos, 'accrualAmount'),
            expanded: false,
            rowId: uuid(),
          };
        }
      );

      return data;
    } catch (error) {
      throw new Error(error);
    }
  }, [companyId, glPeriodId]);

  const { value, execute, isLoading, setValue } = useAsync(call, false);

  return {
    isLoading,
    accrualReport: value,
    setAccrualReport: setValue,
    getAccrualReport: execute,
    rawAccrualData,
  };
};

export default useGetAccrualReportApi;
